// react
import { useEffect, useState } from 'react';

// material ui icons
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';

// material ui
import { Box, Button, Dialog, Typography } from '@mui/material';
import { StaticDateRangePicker } from '@mui/x-date-pickers-pro/StaticDateRangePicker';

// components
import useSearchParams from '@/hooks/useStableSearchParams';

import { DateTime } from 'luxon';
// default range: last 30 days (luxon DateTime)
const defaultRange = () => {
  const to = DateTime.now();
  const from = to.startOf('year');
  return [from, to];
};

const DashboardDateRangeSelector = ({
  paramFrom = 'from',
  paramTo = 'to',
  onApply = null,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlFrom = searchParams.get(paramFrom);
  const urlTo = searchParams.get(paramTo);

  const [value, setValue] = useState(() => {
    if (urlFrom && urlTo)
      return [DateTime.fromISO(urlFrom), DateTime.fromISO(urlTo)];
    return defaultRange();
  });

  // dialog open state + temporary selection inside dialog
  const [open, setOpen] = useState(false);
  const [tempValue, setTempValue] = useState(value);

  useEffect(() => {
    // sync from URL when present
    if (urlFrom && urlTo) {
      const f = DateTime.fromISO(urlFrom);
      const t = DateTime.fromISO(urlTo);
      if (!f.equals(value[0]) || !t.equals(value[1])) setValue([f, t]);
    }

    // if missing in URL, write defaults
    if (!urlFrom || !urlTo) {
      const [defFrom, defTo] = defaultRange();
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (!next.get(paramFrom)) next.set(paramFrom, defFrom.toISODate());
          if (!next.get(paramTo)) next.set(paramTo, defTo.toISODate());
          return next;
        },
        { replace: true },
      );
      setValue([defFrom, defTo]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlFrom, urlTo]);

  // keep tempValue in sync when outer value changes
  useEffect(() => setTempValue(value), [value]);

  const apply = (newValue) => {
    const [f, t] = newValue;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (f) next.set(paramFrom, f.toISODate());
        if (t) next.set(paramTo, t.toISODate());
        return next;
      },
      { replace: true },
    );
    if (onApply) onApply({ from: f?.toISODate(), to: t?.toISODate() });
  };

  const openDialog = () => {
    setTempValue(value);
    setOpen(true);
  };

  const closeDialog = () => setOpen(false);

  const handleApply = () => {
    setValue(tempValue);
    apply(tempValue);
    setOpen(false);
  };

  // Shortcuts for the picker (uses DateTime to compute ranges)
  const shortcutsItems = [
    {
      label: 'Denne uge',
      getValue: () => {
        const n = DateTime.now();
        return [n.startOf('week'), n.endOf('week')];
      },
    },
    {
      label: 'Sidste uge',
      getValue: () => {
        const n = DateTime.now().minus({ weeks: 1 });
        return [n.startOf('week'), n.endOf('week')];
      },
    },
    {
      label: 'Denne måned',
      getValue: () => {
        const n = DateTime.now();
        return [n.startOf('month'), n.endOf('month')];
      },
    },
    {
      label: 'Sidste måned',
      getValue: () => {
        const n = DateTime.now().minus({ months: 1 });
        return [n.startOf('month'), n.endOf('month')];
      },
    },
    {
      label: 'ÅTD',
      getValue: () => {
        const n = DateTime.now();
        return [n.startOf('year'), n];
      },
    },
  ];

  const formatRange = (v) => {
    if (!v) return '';
    const [f, t] = v;
    return `${f?.toLocaleString() ?? '—'} - ${t?.toLocaleString() ?? '—'}`;
  };

  return (
    <Box>
      <Button
        startIcon={<CalendarTodayRoundedIcon />}
        variant="outlined"
        size="small"
        onClick={openDialog}
        data-testid="open-date-range-dialog"
        sx={{
          borderRadius: 2,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          textTransform: 'none',
          p: 0.9,
          color: 'text.primary',
        }}
      >
        <Typography variant="button">{formatRange(value)}</Typography>
      </Button>

      <Dialog open={open} onClose={closeDialog} maxWidth="md">
        <StaticDateRangePicker
          value={tempValue}
          onChange={(newVal) => setTempValue(newVal)}
          displayStaticWrapperAs="mobile"
          calendars={2}
          dayOfWeekFormatter={(day) => day.toLocaleString({ weekday: 'short' })}
          slotProps={{
            shortcuts: {
              items: shortcutsItems,
            },
            actionBar: {
              onAccept: handleApply,
              onCancel: closeDialog,
              actions: ['cancel', 'accept'],
            },
          }}
        />
      </Dialog>
    </Box>
  );
};

export default DashboardDateRangeSelector;
